import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { Dialog } from '../../../@core/services/modal/modal.interfaces';
import { LocaleDatePipe } from '../../../shared/pipes/locale-date.pipe';
import { LabeledDataComponent } from '../../../shared/components/labeled-data/labeled-data.component';
import { DialogButtonsDirective } from '../../../shared/components/modal-dialog/dialog-buttons.directive';
import {
  GetVatIdHistoryForAddressQuery,
  VatIdCheckResult,
  VatIdCheckType,
  VatIdFieldMatch,
} from '../../../generated/generated-types';

export type VatVerificationRow = GetVatIdHistoryForAddressQuery['getVatIdHistoryForAddress'][number];

type TagSeverity = 'success' | 'danger' | 'warn' | 'secondary';

@Component({
  selector: 'app-vat-id-report-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './vat-id-report-dialog.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    TagModule,
    LocaleDatePipe,
    LabeledDataComponent,
    DialogButtonsDirective,
  ],
})
export class VatIdReportDialogComponent implements Dialog<void> {
  resolveWith!: (result?: void) => void;
  verification!: VatVerificationRow;

  resultSeverity(): TagSeverity {
    switch (this.verification?.result) {
      case VatIdCheckResult.VALID:
        return 'success';
      case VatIdCheckResult.INVALID:
        return 'danger';
      default:
        return 'warn';
    }
  }

  matchSeverity(match?: VatIdFieldMatch | null): TagSeverity {
    switch (match) {
      case VatIdFieldMatch.VALID:
        return 'success';
      case VatIdFieldMatch.INVALID:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  matchLabel(match?: VatIdFieldMatch | null): string {
    switch (match) {
      case VatIdFieldMatch.VALID:
        return 'address.vat-match-valid';
      case VatIdFieldMatch.INVALID:
        return 'address.vat-match-invalid';
      default:
        return 'address.vat-match-not-processed';
    }
  }

  resultLabel(): string {
    switch (this.verification?.result) {
      case VatIdCheckResult.VALID:
        return 'address.vat-result-tag-valid';
      case VatIdCheckResult.INVALID:
        return 'address.vat-result-tag-invalid';
      case VatIdCheckResult.ERROR:
        return _('address.vat-result-tag-error');
      default:
        return _('address.vat-result-tag-error');
    }
  }

  checkTypeLabel(): string {
    return this.verification?.checkType === VatIdCheckType.QUALIFIED
      ? 'address.vat-check-type-qualified'
      : 'address.vat-check-type-simple';
  }

  /** VIES timestamp when present; otherwise the row's createdAt so ERROR rows still show a date. */
  requestDate(): Date | string | null {
    return this.verification?.requestDate ?? this.verification?.createdAt ?? null;
  }

  close(): void {
    this.resolveWith();
  }
}
